import qs from "qs";

import {
  ConfigurationProperty,
  Middleware,
} from "@plansoft-configuration-renderer/configuration-api";

export type ButtonType =
  | "SAP"
  | "CAD"
  | "DRAWING"
  | "DOC"
  | "LOAD"
  | "SAVE"
  | "CART"
  | "NEWID";

export interface PropertyValue {
  dataType: string;
  isLanguageDependent: boolean;
  value: boolean;
}
export interface ParsedUrlParams {
  cfgObjectId?: string;
  language?: string;
  uiCulture?: string;
  currencyId?: string;
  params?: string;
  context?: { [key: string]: unknown };
}

/**
 * Helper function to parse and validate session configuration data
 * @param responseText - Raw response text from the API
 * @returns Parsed configuration object with configID and language, or undefined if invalid
 */
const parseSessionConfigurationData = (
  responseText: string,
): { configID: string; language: string } | undefined => {
  try {
    const parsedData = JSON.parse(responseText);

    const hasValidConfigID =
      parsedData &&
      typeof parsedData === "object" &&
      typeof parsedData.configID === "string" &&
      parsedData.configID.length > 0;

    const hasValidLanguage =
      parsedData &&
      typeof parsedData === "object" &&
      typeof parsedData.language === "string" &&
      parsedData.language.length > 0;

    if (hasValidConfigID && hasValidLanguage) {
      return {
        configID: parsedData.configID,
        language: parsedData.language,
      };
    }

    return undefined;
  } catch {
    throw new Error(`Invalid JSON response: ${responseText}`);
  }
};

export interface BodyGenerateDocument {
  configId: string;
  whichDocument: string;
}

export interface ResponseGenerateDocument {
  filename: string;
}

export interface BodyCheckDocument {
  pollUrl: string;
}

export interface ResponseCheckDocument {
  finish: boolean;
  error: boolean;
  msg: string;
  nextCallInSec: number;
}

export interface BodyDownloadDocument {
  pollUrl: string;
}

export interface ResponseDownloadDocument {
  fileStream: string;
}

export interface SaveConfigurationBody {
  description: string;
  title: string;
  shorttext: string;
  sapOrShop: string;
  type: "FAV" | "SC" | "SAP";
}

export interface NewShopIdBody {
  description: string;
  title: string;
  shorttext: string;
}
export interface CadFormData {
  url: string;
  method: string;
  body: Record<string, string | number | boolean>;
}

const SESSION_STORAGE_KEY = "sessionId";
const RESPONSE_SESSION_HEADERS = ["x-session-id", "x-session_id", "x-sess-id"];
const REQUEST_SESSION_HEADER = "x-session-id";

const getSessionIdFromStorage = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const withSessionHeader = (headers: HeadersInit = {}): Headers => {
  const requestHeaders = new Headers(headers);
  const sessionId = getSessionIdFromStorage();

  if (sessionId) {
    requestHeaders.set(REQUEST_SESSION_HEADER, sessionId);
  }

  return requestHeaders;
};

export const persistSessionIdFromResponse = (response: Response): void => {
  if (typeof window === "undefined") {
    return;
  }

  const sessionId = RESPONSE_SESSION_HEADERS.map((header) =>
    response.headers.get(header),
  ).find((value) => Boolean(value));
  if (!sessionId) {
    return;
  }

  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    // Ignore storage write errors (private mode / quota), request flow should continue.
  }
};

/**
 * Middleware for the renderer's ConfigurationApi: injects the stored session id
 * as x-session-id on every request and persists it from every response.
 */
export const sessionMiddleware: Middleware = {
  pre: async ({ url, init }) => {
    return {
      url,
      init: { ...init, headers: withSessionHeader(init.headers) },
    };
  },
  post: async ({ response }) => {
    persistSessionIdFromResponse(response);
    return response;
  },
};

/**
 * Middleware watching the keepAlive heartbeat: the endpoint returns 200 with a
 * boolean body (`false` once the session was cancelled due to inactivity) or a
 * 400 if the configurator can't be reached at all. Both cases invoke `onSessionExpired`.
 */
export const createKeepAliveMiddleware = (
  onSessionExpired: () => void,
): Middleware => ({
  post: async ({ url, response }) => {
    if (!url.includes("/keepAlive")) {
      return;
    }

    if (response.status === 500) {
      onSessionExpired();
      return;
    }

    if (response.ok && (await response.json()) === false) {
      onSessionExpired();
    }
  },
});

export const isButtonVisible = (
  properties: Record<string, ConfigurationProperty> | null | undefined,
  buttonType: ButtonType,
): boolean => {
  const key = `enable.${buttonType}`;
  if (properties?.[key] === undefined) return true;
  return properties[key].value;
};

export const isButtonActive = (
  properties: Record<string, ConfigurationProperty> | null | undefined,
  buttonType: ButtonType,
): boolean => {
  const key = `activate.${buttonType}`;
  if (properties?.[key] === undefined) return false;
  return properties[key].value;
};

export const isTreeExpandedByDefault = (
  properties: Record<string, ConfigurationProperty> | null | undefined,
): boolean => {
  const key = "expand.TREE";
  if (properties?.[key] === undefined) return false;
  return properties[key].value;
};

export function tryParseOutputData<T>(data: Partial<T>): T {
  if ("output" in data && typeof data["output"] === "string") {
    try {
      const result = JSON.parse(data["output"]);
      if (result && typeof result === "object") {
        data["output"] = result;
      }
    } catch (error) {
      console.error("Error parsing output data:", error);
    }
  }
  return data as T;
}

export function handleHasStringValue<T extends { value?: unknown }>(
  configurationValues: T[],
): boolean {
  return configurationValues.some((value) => typeof value.value === "string");
}

export function downloadFile(
  filename: string,
  fileStream: string,
  fileType = "application/pdf",
): void {
  // Dekodiere Base64 zu Blob
  const byteCharacters = atob(fileStream);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: fileType });

  // Download auslösen
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

const isString = (value: unknown): value is string => typeof value === "string";
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const getParsedUrlParams = (): ParsedUrlParams => {
  const searchParams = window.location.search.substring(1);
  const params = qs.parse(searchParams);

  return {
    cfgObjectId: isString(params.cfgObjectId) ? params.cfgObjectId : undefined,
    language: isString(params.language) ? params.language : undefined,
    uiCulture: isString(params.uiCulture) ? params.uiCulture : undefined,
    currencyId: isString(params.currencyId) ? params.currencyId : undefined,
    params: isString(params.params) ? params.params : undefined,
    context: isRecord(params.context) ? params.context : undefined,
  };
};

export function formatNumber(
  value: number | string | undefined,
  culture: string | null | undefined,
): string {
  if (value === undefined || value === null) {
    return "";
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return "";
  }

  try {
    return new Intl.NumberFormat(culture || undefined).format(numValue);
  } catch (error) {
    console.error("Error formatting number:", error);
    return numValue.toString();
  }
}

export const fetchSessionParams = async (
  params: string | undefined,
): Promise<{ configID: string; language: string } | undefined> => {
  if (!params) {
    return undefined;
  }

  try {
    const response = await fetch(
      `/api/v1/pgfirst/configuration?params=${encodeURIComponent(params)}`,
      {
        method: "GET",
        headers: withSessionHeader({
          "Content-Type": "application/json",
        }),
      },
    );

    persistSessionIdFromResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseText = await response.text();

    if (responseText.startsWith("ERROR")) {
      throw new Error(responseText);
    }

    const sessionData = parseSessionConfigurationData(responseText);
    if (sessionData) {
      return sessionData;
    }

    throw new Error(`Invalid session configuration data: ${responseText}`);
  } catch (error) {
    console.error("Error fetching session ID:", error);
    throw error; // [KS] Re-throw to let TanStack Query handle the error state
  }
};

export const fetchLoadedSessionId = async (
  id: string | undefined,
): Promise<string | undefined> => {
  if (!id) {
    return undefined;
  }

  try {
    const response = await fetch("/api/v1/pg/load", {
      method: "POST",
      headers: withSessionHeader({
        "Content-Type": "application/json; X-Version=1.0",
        accept: "*/*",
      }),
      body: JSON.stringify({ id }),
    });

    persistSessionIdFromResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseText = await response.text();

    if (responseText.startsWith("ERROR")) {
      throw new Error(responseText);
    }
    return responseText;
  } catch (error) {
    console.error("Error fetching loaded session ID:", error);
    throw error; // [KS] Re-throw to let TanStack Query handle the error state
  }
};
export const newShopId = async (body: NewShopIdBody): Promise<string> => {
  const response = await fetch("/api/v1/pg/createNewShopId", {
    method: "POST",
    headers: withSessionHeader({
      accept: "*/*",
      "Content-Type": "application/json; X-Version=1.0",
    }),
    body: JSON.stringify(body),
  });

  persistSessionIdFromResponse(response);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return await response.text();
};
export const saveConfiguration = async (
  body: SaveConfigurationBody,
  type: string,
  sessionId?: string,
): Promise<string> => {
  try {
    const response = await fetch("/api/v1/pg/save", {
      method: "POST",
      headers: withSessionHeader({
        accept: "*/*",
        "Content-Type": "application/json; X-Version=1.0",
      }),
      body: JSON.stringify(body),
    });

    persistSessionIdFromResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseText = await response.text();
    if (responseText.startsWith("ERROR")) {
      throw new Error(responseText);
    }
    if (type === "FAV") {
      const message = {
        type: "showprojects",
        extSessionId: sessionId,
      };
      window.parent.postMessage(message, "*");
    } else if (type === "SC") {
      const message = {
        type: "showcart",
      };
      window.parent.postMessage(message, "*");
    }
    if (type === "SAP") return responseText;
    return "";
  } catch (error) {
    console.error("Error saving configuration:", error);
    throw error;
  }
};

export const fetchCadBox = async (): Promise<CadFormData> => {
  try {
    const response = await fetch("/api/v1/pg/cadbox", {
      method: "GET",
      headers: withSessionHeader({
        accept: "*/*",
      }),
    });

    persistSessionIdFromResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseText = await response.text();
    if (responseText.startsWith("ERROR")) {
      throw new Error(responseText);
    }
    return JSON.parse(responseText);
  } catch (error) {
    console.error("Error fetching CAD box:", error);
    throw error;
  }
};

export const fetchGenerateDocument = async (
  body: BodyGenerateDocument,
): Promise<ResponseGenerateDocument> => {
  try {
    const response = await fetch("/api/v1/document/generate", {
      method: "POST",
      headers: withSessionHeader({
        "Content-Type": "application/json; X-Version=1.0",
        accept: "*/*",
      }),
      body: JSON.stringify(body),
      credentials: "include",
    });

    persistSessionIdFromResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseText = await response.text();
    if (responseText.startsWith("ERROR")) {
      throw new Error(responseText);
    }
    return JSON.parse(responseText);
  } catch (error) {
    console.error("Error fetching document generate:", error);
    throw error;
  }
};

export const fetchCheckDocument = async (
  body: BodyCheckDocument,
): Promise<ResponseCheckDocument> => {
  try {
    const response = await fetch("/api/v1/document/check", {
      method: "POST",
      headers: withSessionHeader({
        "Content-Type": "application/json; X-Version=1.0",
        accept: "*/*",
      }),
      body: JSON.stringify(body),
      credentials: "include",
    });

    persistSessionIdFromResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseText = await response.text();
    if (responseText.startsWith("ERROR")) {
      throw new Error(responseText);
    }
    return JSON.parse(responseText);
  } catch (error) {
    console.error("Error fetching document check:", error);
    throw error;
  }
};

export const fetchDownloadDocument = async (
  body: BodyDownloadDocument,
): Promise<ResponseDownloadDocument> => {
  try {
    const response = await fetch(
      "/api/v1/document/download?pollUrl=" + encodeURIComponent(body.pollUrl),
      {
        method: "GET",
        headers: withSessionHeader({
          "Content-Type": "application/json; X-Version=1.0",
          accept: "*/*",
        }),
        credentials: "include",
      },
    );

    persistSessionIdFromResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseText = await response.text();
    if (responseText.startsWith("ERROR")) {
      throw new Error(responseText);
    }
    return JSON.parse(responseText);
  } catch (error) {
    console.error("Error fetching document download:", error);
    throw error;
  }
};

export const resetConfiguration = async (): Promise<
  { configID: string; language: string } | undefined
> => {
  try {
    const response = await fetch("/api/v1/pg/reset", {
      method: "GET",
      headers: withSessionHeader({
        accept: "*/*",
      }),
    });

    persistSessionIdFromResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseText = await response.text();
    if (responseText.startsWith("ERROR")) {
      throw new Error(responseText);
    }

    const sessionData = parseSessionConfigurationData(responseText);
    if (sessionData) {
      return sessionData;
    }

    throw new Error(`Invalid reset configuration data: ${responseText}`);
  } catch (error) {
    console.error("Error resetting configuration:", error);
    throw error;
  }
};

export async function downloadFileFromResponse(res: Response): Promise<string> {
  const urls = await res.json();
  const pdfUrl = urls[0];
  const a = document.createElement("a");
  a.href = pdfUrl;
  a.download = "download.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return pdfUrl;
}

export async function sendAllRequests(
  responseData: CadFormData,
): Promise<string | null> {
  const res = await fetch(responseData.url, {
    method: responseData.method || "POST",
    headers: withSessionHeader({
      "Content-Type": "application/json",
      "x-api-key": "hiwin_prod",
    }),
    body: JSON.stringify(responseData.body),
  });

  persistSessionIdFromResponse(res);

  if (!res.ok) {
    console.error("Failed to fetch stream for download");
    return null;
  }
  return downloadFileFromResponse(res);
}
